import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Wrench } from 'lucide-react';
import logoLight from '@assets/images/scriptix-blue-logo@2-1.png';
import logoDark from '@assets/images/scriptix-white-400x99.png';
import { useThemeContext } from '@utils/hooks/use-theme-context';

/* eslint-disable no-restricted-syntax -- Semantic maintenance/warning amber tint */
const MAINTENANCE_TINT = {
  bg: 'rgba(251, 191, 36, 0.15)',
  fg: '#f59e0b'
};
/* eslint-enable no-restricted-syntax */

/**
 * Maintenance Page
 * Displayed when the system is in maintenance mode (503 response with detail: "maintenance").
 * Full-screen centered layout with no navigation.
 */
export default function Maintenance() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme, getColour } = useThemeContext();

  const logo = theme === 'dark' ? logoDark : logoLight;

  const handleTryAgain = () => {
    navigate('/');
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <main
      className="bg-app min-h-screen w-full flex flex-col items-center justify-center px-6 py-12"
      style={{ color: getColour('normalText') }}
    >
      <div className="text-center max-w-md">
        <div className="mb-8">
          <img src={logo} alt="Scriptix" className="h-10 w-auto mx-auto" />
        </div>

        <div
          className="mx-auto mb-6 h-16 w-16 rounded-full flex items-center justify-center"
          style={{ backgroundColor: MAINTENANCE_TINT.bg }}
        >
          <Wrench className="h-8 w-8" style={{ color: MAINTENANCE_TINT.fg }} />
        </div>

        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl mb-4">
          {t('system-under-maintenance')}
        </h1>

        <p
          className="text-base leading-7 mb-8"
          style={{ color: getColour('inactiveText') }}
        >
          {t('maintenance-page-description')}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={handleRefresh}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white shadow-card hover:opacity-90 transition-opacity"
          >
            <RefreshCw className="h-4 w-4" />
            {t('try-again')}
          </button>
          <button
            onClick={handleTryAgain}
            className="inline-flex items-center gap-2 rounded-full border border-hairline bg-app-soft px-5 py-2 text-sm font-semibold hover:opacity-90 transition-opacity"
            style={{ color: getColour('normalText') }}
          >
            {t('go-to-home')}
          </button>
        </div>

        <p
          className="mt-8 text-xs"
          style={{ color: getColour('inactiveText') }}
        >
          {t('maintenance-footer-note')}
        </p>
      </div>
    </main>
  );
}
